import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DarkPage } from "@/components/app/PageHeader";
import { Bookmark, Users, Lock, Trash2, ExternalLink } from "lucide-react";

const VIEW_TYPE_META = {
  my_tasks: { label: "My Tasks", path: "/my-tasks" },
};

function labelFor(viewType) {
  return VIEW_TYPE_META[viewType]?.label || viewType;
}

function pathFor(view) {
  const base = VIEW_TYPE_META[view.view_type]?.path;
  if (!base) return null;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(view.filters || {})) {
    if (v !== "" && v != null) params.set(k, v);
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export default function SavedViews() {
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/saved-views");
      setViews(data || []);
    } catch {
      toast.error("Failed to load saved views");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleShare = async (v) => {
    try {
      await api.put(`/saved-views/${v.view_id}`, { is_shared: !v.is_shared });
      toast.success(v.is_shared ? "Now private" : "Shared with your team");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update saved view");
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete saved view "${v.name}"?`)) return;
    try {
      await api.delete(`/saved-views/${v.view_id}`);
      toast.success("Saved view deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete saved view");
    }
  };

  const open = (v) => {
    const path = pathFor(v);
    if (!path) return toast.error("This view type isn't linked to a page yet");
    navigate(path);
  };

  const mine = views.filter(v => v.is_mine);
  const shared = views.filter(v => !v.is_mine);

  return (
    <DarkPage><div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Saved Views</h1>
        <p className="text-sm text-[#7d8590] mt-1">Filter presets you've saved from list pages — reopen them instantly, or share one with your whole team.</p>
      </div>

      {loading ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">Loading…</div>
      ) : views.length === 0 ? (
        <div className="text-center py-20 text-[#7d8590] text-sm">
          <Bookmark className="w-8 h-8 mx-auto mb-2 text-[#30363d]" />
          No saved views yet. Look for "Save this view" on a filterable list page (e.g. My Tasks).
        </div>
      ) : (
        <div className="space-y-6">
          {mine.length > 0 && (
            <section>
              <h2 className="text-xs uppercase tracking-wide text-[#7d8590] mb-2">My views</h2>
              <div className="space-y-2">
                {mine.map(v => (
                  <div key={v.view_id} className="flex items-center justify-between gap-3 bg-[#0d1117] border border-[#21262d] rounded-lg px-4 py-3">
                    <button onClick={() => open(v)} className="flex items-center gap-2 min-w-0 text-left group">
                      <Bookmark className="w-4 h-4 text-[#00F5D4] flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm text-white truncate group-hover:underline">{v.name}</div>
                        <div className="text-[10px] text-[#7d8590]">{labelFor(v.view_type)}</div>
                      </div>
                    </button>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => toggleShare(v)} title={v.is_shared ? "Shared with team — click to make private" : "Private — click to share with team"}
                        className={`p-1.5 rounded-md ${v.is_shared ? "text-[#00F5D4]" : "text-[#7d8590] hover:text-white"}`}>
                        {v.is_shared ? <Users className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => open(v)} className="p-1.5 text-[#7d8590] hover:text-white" title="Open"><ExternalLink className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(v)} className="p-1.5 text-[#7d8590] hover:text-rose-400" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
          {shared.length > 0 && (
            <section>
              <h2 className="text-xs uppercase tracking-wide text-[#7d8590] mb-2">Shared by your team</h2>
              <div className="space-y-2">
                {shared.map(v => (
                  <div key={v.view_id} className="flex items-center justify-between gap-3 bg-[#0d1117] border border-[#21262d] rounded-lg px-4 py-3">
                    <button onClick={() => open(v)} className="flex items-center gap-2 min-w-0 text-left group">
                      <Users className="w-4 h-4 text-[#7d8590] flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm text-white truncate group-hover:underline">{v.name}</div>
                        <div className="text-[10px] text-[#7d8590]">{labelFor(v.view_type)} · by {v.owner_name}</div>
                      </div>
                    </button>
                    <button onClick={() => open(v)} className="p-1.5 text-[#7d8590] hover:text-white flex-shrink-0" title="Open"><ExternalLink className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div></DarkPage>
  );
}
